import React, { useState, useEffect } from 'react';
import { useConfirm } from "../../hooks/useConfirm";
import { usuariosAPI, niveisAcessoAPI, clientesAPI } from '../../services/api';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import UserBadge from '../../components/UserBadge';
import { Users, Plus, Edit, Trash2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNivelAcesso } from '../../hooks/useNivelAcesso';
import { PageSkeleton, SkeletonTabela } from '../../components/Skeleton';

const AdminUsers = () => {
  const showConfirm = useConfirm();
  const { user, getNiveisDisponiveis } = useNivelAcesso();
  const { darkMode } = useTheme();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [niveis, setNiveis] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    senha: '',
    nivel_acesso: 'usuario',
    status: true,
    cliente_id: ''
  });

  useEffect(() => {
    loadData();
    loadNiveis();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, clientsRes] = await Promise.all([
        usuariosAPI.list(),
        clientesAPI.list()
      ]);
      setUsers(usersRes.data.usuarios || []);
      setClients(clientsRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadNiveis = async () => {
    try {
      const response = await niveisAcessoAPI.getDescricoes();
      setNiveis(response.data);
    } catch (error) {
      console.error('Erro ao carregar níveis:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      if (editingUser) {
        const updateData = { ...data, id: editingUser.id };
        if (!updateData.senha) delete updateData.senha;
        await usuariosAPI.update(editingUser.id, updateData);
      } else {
        await usuariosAPI.create(data);
      }
      
      toast.success('Usuário salvo com sucesso');
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar');
    }
  };

  const handleDelete = async (id) => {
    showConfirm({
      title: 'Deletar Usuário',
      message: 'Tem certeza que deseja deletar este usuário?',
      confirmText: 'Deletar',
      type: 'danger',
      onConfirm: async () => {
        try {
          await usuariosAPI.delete(id);
          toast.success('Usuário deletado com sucesso');
          loadData();
        } catch (error) {
          toast.error('Erro ao deletar');
        }
      }
    });
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      nome: user.nome || '',
      email: user.email || '',
      telefone: user.telefone || '',
      senha: '',
      nivel_acesso: user.nivel_acesso || 'usuario',
      cliente_id: user.cliente_id || '',
      status: user.status !== false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      email: '',
      telefone: '',
      senha: '',
      nivel_acesso: 'usuario',
      status: true,
      cliente_id: ''
    });
    setEditingUser(null);
  };

  const openModal = () => {
    resetForm();
    setShowModal(true);
  };
  if (loading) {
    return (
      <Layout>
        <PageSkeleton title="Gerenciar Usuários">
          <SkeletonTabela colunas={6} linhas={8} />
        </PageSkeleton>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className={`text-2xl font-bold flex items-center ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              <Users className="mr-2" />
              Gerenciar Usuários
            </h1>
            <p className={`mt-1 text-sm ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Administre usuários do sistema
            </p>
          </div>
          <button
            onClick={openModal}
            className="gradient-button text-white px-4 py-2 rounded flex items-center"
          >
            <Plus className="mr-2 h-4 w-4" />
            Novo Usuário
          </button>
        </div>

        <div className={`rounded-lg shadow-lg overflow-hidden border ${
          darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <table className="min-w-full">
            <thead className={`${
              darkMode ? 'bg-zinc-700 border-b border-gray-600' : 'bg-gray-50 border-b border-gray-200'
            }`}>
              <tr>
                <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Nome</th>
                <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Email</th>
                <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Empresa</th>
                <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Nível</th>
                <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Status</th>
                <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Ações</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${
              darkMode ? 'bg-zinc-800 divide-gray-700' : 'bg-white divide-gray-200'
            }`}>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="6" className={`px-6 py-12 text-center ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <Users className="mx-auto h-12 w-12 mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">Nenhum usuário encontrado</p>
                    <p className="text-sm">Clique em "Novo Usuário" para adicionar o primeiro usuário.</p>
                  </td>
                </tr>
              ) : (
                users.map((user) => {
                const empresa = user.cliente_id ? 
                  (typeof user.cliente_id === 'object' ? user.cliente_id.nome : 
                   clients.find(c => c.id === user.cliente_id)?.nome) : 'N/A';
                
                return (
                <tr key={user.id} className={`hover:${
                  darkMode ? 'bg-zinc-700' : 'bg-gray-50'
                } transition-colors`}>
                  <td className={`px-6 py-4 whitespace-nowrap font-medium ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {user.nome}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap ${
                    darkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {user.email}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap font-medium ${
                    darkMode ? 'text-blue-400' : 'text-blue-600'
                  }`}>
                    {empresa}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <UserBadge nivel={user.nivel_acesso || 'usuario'} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      user.status 
                        ? darkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800'
                        : darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.status ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleEdit(user)}
                        className={`p-1 rounded-md transition-colors ${
                          darkMode 
                            ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-900/20' 
                            : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                        }`}
                        title="Editar usuário"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        className={`p-1 rounded-md transition-colors ${
                          darkMode 
                            ? 'text-red-400 hover:text-red-300 hover:bg-red-900/20' 
                            : 'text-red-600 hover:text-red-800 hover:bg-red-50'
                        }`}
                        title="Deletar usuário"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`rounded-lg p-6 w-full max-w-md ${
              darkMode ? 'bg-zinc-800' : 'bg-white'
            }`}>
              <h2 className={`text-xl font-bold mb-4 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {editingUser ? 'Editar' : 'Novo'} Usuário
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-white' : 'text-gray-700'
                  }`}>Nome</label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${
                      darkMode
                        ? 'border-gray-600 bg-zinc-700 text-white'
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-white' : 'text-gray-700'
                  }`}>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${
                      darkMode
                        ? 'border-gray-600 bg-zinc-700 text-white'
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-white' : 'text-gray-700'
                  }`}>Telefone</label>
                  <input
                    type="text"
                    value={formData.telefone}
                    onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${
                      darkMode
                        ? 'border-gray-600 bg-zinc-700 text-white'
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-white' : 'text-gray-700'
                  }`}>Senha</label>
                  <input
                    type="password"
                    value={formData.senha}
                    onChange={(e) => setFormData({...formData, senha: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${
                      darkMode
                        ? 'border-gray-600 bg-zinc-700 text-white placeholder-gray-400'
                        : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                    }`}
                    required={!editingUser}
                    placeholder={editingUser ? 'Deixe em branco para manter' : ''}
                  />
                </div>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-white' : 'text-gray-700'
                  }`}>Nível de Acesso</label>
                  <select
                    value={formData.nivel_acesso}
                    onChange={(e) => setFormData({...formData, nivel_acesso: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${
                      darkMode
                        ? 'border-gray-600 bg-zinc-700 text-white'
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  >
                    {getNiveisDisponiveis().map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-white' : 'text-gray-700'
                  }`}>Cliente</label>
                  <select
                    value={formData.cliente_id}
                    onChange={(e) => setFormData({...formData, cliente_id: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${
                      darkMode
                        ? 'border-gray-600 bg-zinc-700 text-white'
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                    required
                  >
                    <option value="">Selecione um cliente</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className={`flex items-center ${
                    darkMode ? 'text-white' : 'text-gray-700'
                  }`}>
                    <input
                      type="checkbox"
                      checked={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.checked})}
                      className="mr-2 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    Ativo
                  </label>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className={`px-4 py-2 border rounded transition-colors ${
                      darkMode
                        ? 'text-gray-300 border-gray-600 hover:bg-zinc-700'
                        : 'text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="gradient-button px-4 py-2 text-white rounded"
                  >
                    {editingUser ? 'Atualizar' : 'Criar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminUsers;